import {
  Journey,
  JourneyRefs,
  PlannerAutomation,
  PlannerTabTitle,
  Position,
  Rect,
} from "./journeyPlanner.types";
import { AutomationStatus } from "../../config/enums";

const JOURNEY_CARD_WIDTH = 400;
const JOURNEY_CARD_MIN_HEIGHT = 200;
const JOURNEY_ALIGNMENT_SNAP_DISTANCE = 16;
const JOURNEY_CARD_GAP = 24;
const JOURNEY_PLANNER_DEFAULT_ZOOM = 1;
const JOURNEY_PLANNER_MIN_ZOOM = 0.5;
const JOURNEY_PLANNER_MAX_ZOOM = 1.5;
const JOURNEY_PLANNER_ZOOM_STEP = 0.1;

function getJourneyPlannerTab(
  automation?: Pick<PlannerAutomation, "Type" | "ScheduleMode"> | null
): PlannerTabTitle {
  if (!automation) {
    return "Daily";
  }

  if (automation.Type === 2) {
    return "Triggered";
  }

  const scheduleMode = automation.ScheduleMode
    ? automation.ScheduleMode.toLowerCase()
    : "";

  if (
    scheduleMode === "fixture" ||
    scheduleMode === "fixturerelative"
  ) {
    return "Fixture";
  }

  return "Daily";
}

function isAutomationDisabled(status?: number | string | null) {
  return (
    String(status || "") !== String(AutomationStatus.Published)
  );
}

function getFilteredAutomations(
  automations: PlannerAutomation[] | undefined,
  activeTab: string
) {
  return (automations || []).filter(
    (automation) => getJourneyPlannerTab(automation) === activeTab
  );
}

function getJourneyCardRect(
  journey: Journey,
  node?: HTMLElement | null,
  nextPosition?: Position
): Rect {
  const width = node?.offsetWidth || JOURNEY_CARD_WIDTH;
  const height = node?.offsetHeight || JOURNEY_CARD_MIN_HEIGHT;
  const x = nextPosition ? nextPosition.x : journey.X ?? journey.x ?? 0;
  const y = nextPosition ? nextPosition.y : journey.Y ?? journey.y ?? 0;

  return {
    left: x,
    top: y,
    right: x + width,
    bottom: y + height,
  };
}

function doJourneyCardsOverlap(first: Rect, second: Rect) {
  return (
    first.left < second.right &&
    first.right > second.left &&
    first.top < second.bottom &&
    first.bottom > second.top
  );
}

function wouldJourneyOverlap(
  journeys: Journey[],
  movingJourneyId: number,
  nextPosition: Position,
  refs: JourneyRefs
) {
  const movingJourney = journeys.find((journey) => journey.Id === movingJourneyId);
  if (!movingJourney) {
    return false;
  }

  const movingRect = getJourneyCardRect(
    movingJourney,
    refs.current[movingJourneyId],
    nextPosition
  );

  return journeys.some((journey) => {
    if (journey.Id === movingJourneyId) {
      return false;
    }

    const otherRect = getJourneyCardRect(
      journey,
      refs.current[journey.Id]
    );

    return doJourneyCardsOverlap(movingRect, otherRect);
  });
}

function getJourneyLayoutAdjustedJourneys(
  journeys: Journey[],
  refs: JourneyRefs,
  gap = JOURNEY_CARD_GAP
): Journey[] {
  let adjustedJourneys = journeys.map((journey) => ({
    ...journey,
    X: Math.round(journey.X ?? journey.x ?? 0),
    Y: Math.round(journey.Y ?? journey.y ?? 0),
  }));
  let changed = false;
  let hasMoved = true;
  let attempts = 0;

  while (hasMoved && attempts < adjustedJourneys.length * 2) {
    hasMoved = false;
    attempts += 1;

    const orderedJourneys = [...adjustedJourneys].sort(
      (first, second) =>
        first.Y - second.Y || first.X - second.X || first.Id - second.Id
    );

    for (let blockerIndex = 0; blockerIndex < orderedJourneys.length; blockerIndex += 1) {
      const blocker = orderedJourneys[blockerIndex];
      const blockerRect = getJourneyCardRect(
        blocker,
        refs.current[blocker.Id]
      );

      for (
        let candidateIndex = blockerIndex + 1;
        candidateIndex < orderedJourneys.length;
        candidateIndex += 1
      ) {
        const candidate = orderedJourneys[candidateIndex];
        const candidateRect = getJourneyCardRect(
          candidate,
          refs.current[candidate.Id]
        );

        if (!doJourneyCardsOverlap(blockerRect, candidateRect)) {
          continue;
        }

        const nextY = Math.round(blockerRect.bottom + gap);
        adjustedJourneys = adjustedJourneys.map((journey) =>
          journey.Id === candidate.Id
            ? {
                ...journey,
                Y: nextY,
              }
            : journey
        );
        orderedJourneys[candidateIndex] = {
          ...candidate,
          Y: nextY,
        };
        changed = true;
        hasMoved = true;
      }
    }
  }

  return changed ? adjustedJourneys : journeys;
}

function getJourneyAlignmentSnappedPosition(
  journeys: Journey[],
  movingJourneyId: number,
  nextPosition: Position
): Position {
  if ((journeys || []).length <= 1) {
    return nextPosition;
  }

  return journeys
    .filter((journey) => journey.Id !== movingJourneyId)
    .reduce(
      (position, journey) => {
        const journeyX = Math.round(journey.X ?? journey.x ?? 0);
        const journeyY = Math.round(journey.Y ?? journey.y ?? 0);
        const shouldSnapX =
          Math.abs(position.x - journeyX) <=
          JOURNEY_ALIGNMENT_SNAP_DISTANCE;
        const shouldSnapY =
          Math.abs(position.y - journeyY) <=
          JOURNEY_ALIGNMENT_SNAP_DISTANCE;

        return {
          x: shouldSnapX ? journeyX : position.x,
          y: shouldSnapY ? journeyY : position.y,
        };
      },
      { ...nextPosition }
    );
}

function getJourneyPlannerDropPosition({
  draggedPosition,
  journey,
  journeyRefs,
  journeys,
  snappedPosition,
}: {
  draggedPosition: Position;
  journey: Journey;
  journeyRefs: JourneyRefs;
  journeys: Journey[];
  snappedPosition: Position;
}) {
  const candidates = [
    snappedPosition,
    { x: snappedPosition.x, y: draggedPosition.y },
    { x: draggedPosition.x, y: snappedPosition.y },
    draggedPosition,
  ];
  const uniqueCandidates = candidates.filter(
    (candidate, index) =>
      candidates.findIndex(
        (currentCandidate) =>
          currentCandidate.x === candidate.x &&
          currentCandidate.y === candidate.y
      ) === index
  );

  return uniqueCandidates.find(
    (candidate) =>
      !wouldJourneyOverlap(
        journeys,
        journey.Id,
        candidate,
        journeyRefs
      )
  );
}

function getJourneyAlignmentGuides(
  journeys: Journey[],
  movingJourneyId: number,
  nextPosition: Position
): { x?: number; y?: number } {
  if ((journeys || []).length <= 1) {
    return {};
  }

  const guides: { x?: number; y?: number } = {};

  journeys
    .filter((journey) => journey.Id !== movingJourneyId)
    .forEach((journey) => {
      const journeyX = Math.round(journey.X ?? journey.x ?? 0);
      const journeyY = Math.round(journey.Y ?? journey.y ?? 0);

      if (
        guides.x === undefined &&
        Math.abs(nextPosition.x - journeyX) <=
          JOURNEY_ALIGNMENT_SNAP_DISTANCE
      ) {
        guides.x = journeyX;
      }

      if (
        guides.y === undefined &&
        Math.abs(nextPosition.y - journeyY) <=
          JOURNEY_ALIGNMENT_SNAP_DISTANCE
      ) {
        guides.y = journeyY;
      }
    });

  return guides;
}

function getJourneyPlannerCanvasSize(
  journeys: Journey[],
  refs: JourneyRefs,
  activePosition?: (Position & { journeyId: number }) | null
) {
  const cardRects = (journeys || []).map((journey) => {
    const node = refs.current[journey.Id];
    const width = node?.offsetWidth || JOURNEY_CARD_WIDTH;
    const height = node?.offsetHeight || JOURNEY_CARD_MIN_HEIGHT;
    const position = {
      x: journey.X ?? journey.x ?? 0,
      y: journey.Y ?? journey.y ?? 0,
    };
    const activeJourneyPosition =
      activePosition && activePosition.journeyId === journey.Id
        ? activePosition
        : null;

    return {
      height,
      width,
      x: Math.max(position.x, activeJourneyPosition?.x ?? position.x),
      y: Math.max(position.y, activeJourneyPosition?.y ?? position.y),
    };
  });
  const edgePadding = cardRects.reduce(
    (padding, cardRect) => ({
      height: Math.max(padding.height, cardRect.height),
      width: Math.max(padding.width, cardRect.width),
    }),
    {
      height: JOURNEY_CARD_MIN_HEIGHT,
      width: JOURNEY_CARD_WIDTH,
    }
  );
  const bounds = cardRects.reduce(
    (size, journey) => {
      return {
        height: Math.max(
          size.height,
          journey.y + journey.height + edgePadding.height
        ),
        width: Math.max(
          size.width,
          journey.x + journey.width + edgePadding.width
        ),
      };
    },
    { height: 0, width: 0 }
  );

  return {
    height: Math.max(
      bounds.height,
      JOURNEY_CARD_MIN_HEIGHT + edgePadding.height
    ),
    width: Math.max(
      bounds.width,
      JOURNEY_CARD_WIDTH + edgePadding.width
    ),
  };
}

function clampJourneyPlannerZoomLevel(zoomLevel: number) {
  return Math.min(
    JOURNEY_PLANNER_MAX_ZOOM,
    Math.max(JOURNEY_PLANNER_MIN_ZOOM, zoomLevel)
  );
}

function getNextJourneyPlannerZoomLevel(
  zoomLevel: number,
  direction: "in" | "out"
) {
  const nextZoomLevel =
    zoomLevel +
    (direction === "out"
      ? -JOURNEY_PLANNER_ZOOM_STEP
      : JOURNEY_PLANNER_ZOOM_STEP);

  return Number(clampJourneyPlannerZoomLevel(nextZoomLevel).toFixed(2));
}

function getJourneyPlannerScaledDelta(
  delta: Partial<Position> | undefined,
  zoomLevel: number
): Position {
  const safeZoomLevel = zoomLevel || JOURNEY_PLANNER_DEFAULT_ZOOM;

  return {
    x: (delta?.x || 0) / safeZoomLevel,
    y: (delta?.y || 0) / safeZoomLevel,
  };
}

export {
  doJourneyCardsOverlap,
  JOURNEY_PLANNER_DEFAULT_ZOOM,
  JOURNEY_PLANNER_MAX_ZOOM,
  JOURNEY_PLANNER_MIN_ZOOM,
  JOURNEY_CARD_MIN_HEIGHT,
  JOURNEY_CARD_WIDTH,
  getFilteredAutomations,
  getJourneyAlignmentGuides,
  getJourneyAlignmentSnappedPosition,
  getJourneyCardRect,
  getJourneyLayoutAdjustedJourneys,
  getJourneyPlannerCanvasSize,
  getJourneyPlannerDropPosition,
  getJourneyPlannerTab,
  getJourneyPlannerScaledDelta,
  getNextJourneyPlannerZoomLevel,
  isAutomationDisabled,
  wouldJourneyOverlap,
};
